import React from 'react';

import { Alert, Button, Card, Descriptions, Flex, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { EpdOperatorState, EpdTitleCode, EpdTitleSchema } from '@app/models/orders';
import { formatDateAndTime, fromUtc } from '@app/services/date';
import { useEpdFileDownload } from '@app/services/hooks/useEpdFileDownload';
import { EpdError } from '@components/EpdError';

import { titleSigner } from '../../services';
import { OperatorStatus } from '../OperatorStatus';

interface IProps {
  title: EpdTitleSchema;
  operatorState?: NonNullable<EpdOperatorState['titles']>[EpdTitleCode];
}

export const TitleCard: React.FC<IProps> = ({ title, operatorState }) => {
  const epdT = useTranslation(NSKeys.epd);
  const download = useEpdFileDownload();
  const signer = titleSigner(title.signer);
  return (
    <Card
      size="small"
      title={epdT.t(`titles.${title.title}` as const)}
      extra={<Tag>{epdT.t(`titleStatuses.${title.status}` as const)}</Tag>}
    >
      <Flex vertical gap={12}>
        <Descriptions
          size="small"
          column={1}
          items={[
            {
              key: 'status_changed_at',
              label: epdT.t('statusChangedAt'),
              children: formatDateAndTime(fromUtc(title.status_changed_at)),
            },
            ...(signer.name
              ? [
                  {
                    key: 'signer',
                    label: epdT.t('signer'),
                    children: [signer.name, signer.position].filter(Boolean).join(', '),
                  },
                ]
              : []),
            ...(signer.numbers.length
              ? [{ key: 'poa', label: epdT.t('ground'), children: signer.numbers.join(', ') }]
              : []),
            ...(title.sent_at
              ? [{ key: 'sent_at', label: epdT.t('sentAt'), children: formatDateAndTime(fromUtc(title.sent_at)) }]
              : []),
            ...(operatorState?.poa
              ? [
                  {
                    key: 'poa_verdict',
                    label: epdT.t('poaVerdict'),
                    children: (
                      <OperatorStatus
                        severity={operatorState.poa.severity}
                        text={operatorState.poa.text || operatorState.poa.status || operatorState.poa.error_code}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
        {title.title === EpdTitleCode.Receipt && operatorState && (
          <Alert
            type={operatorState.delivered ? 'success' : 'info'}
            showIcon
            title={epdT.t(operatorState.delivered ? 'receiptDelivered' : 'receiptNotDelivered')}
          />
        )}
        <Space>
          {title.has_file && (
            <Button
              loading={download.isLoading && download.variables?.kind === 'file'}
              disabled={download.isLoading}
              onClick={() =>
                download.mutate({ uid: title.uid, kind: 'file', fallbackName: title.file_name || `${title.uid}.xml` })
              }
            >
              {epdT.t('downloadXml')}
            </Button>
          )}
          {title.has_signature && (
            <Button
              loading={download.isLoading && download.variables?.kind === 'signature'}
              disabled={download.isLoading}
              onClick={() =>
                download.mutate({
                  uid: title.uid,
                  kind: 'signature',
                  fallbackName: `${title.file_name || title.uid}.sig`,
                })
              }
            >
              {epdT.t('downloadSignature')}
            </Button>
          )}
        </Space>
        {download.isError && <EpdError error={download.error} />}
      </Flex>
    </Card>
  );
};
